//frontend/src/pages/company/CompanyDashboard.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { companyAPI, deliveryAPI } from '../../services/api';

const CompanyDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Dashboard stats
  const [stats, setStats] = useState({
    warehouses: 0,
    shops: 0,
    drivers: 0,
    todayDeliveries: 0,
    todayCompleted: 0,
    todayPending: 0,
    todayInTransit: 0,
    unpaidCommission: 0
  });

  // 7 хоногийн статистик
  const [weeklyStats, setWeeklyStats] = useState([]);
  
  // Шилдэг шопууд
  const [topShops, setTopShops] = useState([]);
  
  // COD мэдээлэл
  const [codStats, setCodStats] = useState({
    totalCod: 0,
    paidAmount: 0,
    pendingAmount: 0,
    balance: 0
  });

  // Сүүлийн хүргэлтүүд
  const [recentDeliveries, setRecentDeliveries] = useState([]);

  useEffect(() => {
    if (user?.company_id) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // 1. Компанийн үндсэн мэдээлэл
      const companyResponse = await companyAPI.getById(user.company_id);
      const companyData = companyResponse.data.data;
      
      setStats({
        warehouses: companyData.stats?.warehouseCount || 0,
        shops: companyData.stats?.shopCount || 0,
        drivers: companyData.stats?.driverCount || 0,
        todayDeliveries: companyData.stats?.todayDeliveries || 0,
        todayCompleted: companyData.stats?.todayCompleted || 0,
        todayPending: companyData.stats?.todayPending || 0,
        todayInTransit: companyData.stats?.todayInTransit || 0,
        unpaidCommission: companyData.stats?.unpaidCommission || 0
      });

      // 2. 7 хоногийн хүргэлт статистик
      try {
        const weeklyResponse = await deliveryAPI.getAll({ 
          date_range: 'week',
          limit: 1000 
        });
        
        if (weeklyResponse.data.success) {
          // Өдөр бүрээр группдох
          const deliveries = weeklyResponse.data.data || [];
          const dailyMap = {};
          
          // Сүүлийн 7 өдрийг үүсгэх
          const days = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба'];
          for (let i = 6; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            const dayName = days[date.getDay()];
            dailyMap[dateStr] = { 
              day: dayName, 
              date: dateStr,
              total: 0, 
              completed: 0 
            };
          }
          
          // Хүргэлтүүдийг өдрөөр нь тоолох
          deliveries.forEach(d => {
            const dateStr = d.created_at?.split('T')[0];
            if (dailyMap[dateStr]) {
              dailyMap[dateStr].total++;
              if (d.status === 'delivered') {
                dailyMap[dateStr].completed++;
              }
            }
          });
          
          setWeeklyStats(Object.values(dailyMap));
        }
      } catch (e) {
        console.log('Weekly stats not available');
      }

      // 3. Шилдэг шопууд (хүргэлтийн тоогоор)
      try {
        const shopsResponse = await companyAPI.getShops(user.company_id);
        if (shopsResponse.data.success) {
          const shops = shopsResponse.data.data || [];
          // Хүргэлтийн тоогоор эрэмблэх (хэрэв байвал)
          const sortedShops = shops
            .slice(0, 5)
            .map(shop => ({
              id: shop.id,
              name: shop.shop_name,
              deliveries: shop.delivery_count || 0,
              status: shop.status
            }));
          setTopShops(sortedShops);
        }
      } catch (e) {
        console.log('Shops not available');
      }

      // 4. COD тооцоо
      try {
        const codResponse = await fetch(`/api/v1/company/cod-settlements?date_range=month`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const codData = await codResponse.json();
        if (codData.success) {
          setCodStats({
            totalCod: codData.data.summary?.total_cod || 0,
            paidAmount: codData.data.summary?.paid_amount || 0,
            pendingAmount: codData.data.summary?.pending_amount || 0,
            balance: codData.data.summary?.balance || 0
          });
        }
      } catch (e) {
        console.log('COD stats not available');
      }

      // 5. Сүүлийн хүргэлтүүд
      try {
        const recentResponse = await deliveryAPI.getAll({ 
          limit: 5,
          date_range: 'today'
        });
        if (recentResponse.data.success) {
          setRecentDeliveries(recentResponse.data.data?.slice(0, 5) || []);
        }
      } catch (e) {
        console.log('Recent deliveries not available');
      }

    } catch (error) {
      console.error('Dashboard өгөгдөл татахад алдаа:', error);
    } finally {
      setLoading(false);
    }
  };

  // Тооцоолол
  const successRate = stats.todayDeliveries > 0 
    ? Math.round((stats.todayCompleted / stats.todayDeliveries) * 100) 
    : 0;

  // Статусын өнгө
  const getStatusColor = (status) => {
    const colors = {
      'delivered': 'bg-green-100 text-green-700',
      'in_transit': 'bg-blue-100 text-blue-700',
      'pending': 'bg-yellow-100 text-yellow-700',
      'cancelled': 'bg-red-100 text-red-700',
      'postponed': 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusText = (status) => {
    const texts = {
      'delivered': 'Хүргэсэн',
      'in_transit': 'Хүргэж байна',
      'pending': 'Хүлээгдэж буй',
      'cancelled': 'Цуцалсан',
      'postponed': 'Хойшилсон'
    };
    return texts[status] || status;
  };

  // Цаг форматлах
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Дөнгөж сая';
    if (diffMins < 60) return `${diffMins} минутын өмнө`;
    if (diffHours < 24) return `${diffHours} цагийн өмнө`;
    return date.toLocaleDateString('mn-MN');
  };

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-500">Өгөгдөл татаж байна...</p>
          </div>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* ============================================ */}
        {/* STAT CARDS */}
        {/* ============================================ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Агуулах */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition cursor-pointer" onClick={() => navigate('/company/warehouses')}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-3xl">🏭</span>
              </div>
              <button className="text-blue-600 hover:bg-blue-50 rounded-lg px-3 py-1 text-sm font-medium transition">Харах →</button>
            </div>
            <h3 className="text-gray-500 text-sm mb-1">Агуулахууд</h3>
            <p className="text-3xl font-bold text-gray-900 mb-2">{stats.warehouses}</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full font-medium">Идэвхтэй</span>
              <button onClick={(e) => { e.stopPropagation(); navigate('/company/warehouses?action=new'); }} className="text-xs text-blue-600 hover:underline">+ Нэмэх</button>
            </div>
          </div>

          {/* Онлайн шопууд */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition cursor-pointer" onClick={() => navigate('/company/shops')}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                <span className="text-3xl">🛍️</span>
              </div>
              <button className="text-purple-600 hover:bg-purple-50 rounded-lg px-3 py-1 text-sm font-medium transition">Харах →</button>
            </div>
            <h3 className="text-gray-500 text-sm mb-1">Онлайн шопууд</h3>
            <p className="text-3xl font-bold text-gray-900 mb-2">{stats.shops}</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full font-medium">{stats.shops} бүртгэлтэй</span>
            </div>
          </div>

          {/* Хүргэлтүүд */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition cursor-pointer" onClick={() => navigate('/company/deliveries')}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-3xl">📦</span>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Өнөөдөр</p>
                <p className="text-2xl font-bold text-gray-900">{stats.todayDeliveries}</p>
              </div>
            </div>
            <h3 className="text-gray-500 text-sm mb-2">Хүргэлтүүд</h3>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">✅ Хүргэсэн</span>
                <span className="font-semibold text-green-600">{stats.todayCompleted}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">🚚 Хүргэж байна</span>
                <span className="font-semibold text-blue-600">{stats.todayInTransit}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">⏰ Хүлээгдэж буй</span>
                <span className="font-semibold text-yellow-600">{stats.todayPending}</span>
              </div>
            </div>
            <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full transition-all duration-500" style={{ width: `${successRate}%` }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-1 text-right">{successRate}% амжилттай</p>
          </div>

          {/* COD тооцоо */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition cursor-pointer" onClick={() => navigate('/company/settlements')}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center">
                <span className="text-3xl">💰</span>
              </div>
              <button className="text-yellow-600 hover:bg-yellow-50 rounded-lg px-3 py-1 text-sm font-medium transition">Дэлгэрэнгүй</button>
            </div>
            <h3 className="text-gray-500 text-sm mb-1">COD төлөх үлдэгдэл</h3>
            <p className="text-2xl font-bold text-gray-900 mb-1">{codStats.balance.toLocaleString()}₮</p>
            <div className="space-y-1 text-xs mt-3">
              <div className="flex justify-between text-gray-600">
                <span>Нийт COD:</span>
                <span className="font-medium">{codStats.totalCod.toLocaleString()}₮</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Төлсөн:</span>
                <span className="font-medium text-green-600">{codStats.paidAmount.toLocaleString()}₮</span>
              </div>
              {codStats.pendingAmount > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Хүлээгдэж буй:</span>
                  <span className="font-medium text-orange-600">{codStats.pendingAmount.toLocaleString()}₮</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* QUICK ACTIONS */}
        {/* ============================================ */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold mb-2">⚡ Түргэн үйлдлүүд</h3>
              <p className="text-blue-100 text-sm">Ямар үйлдэл хийх вэ?</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/company/deliveries')} className="btn bg-white text-blue-600 hover:bg-blue-50 shadow-lg">
                <span>📦</span>
                <span>Хүргэлтүүд</span>
              </button>
              <button onClick={() => navigate('/company/driver-management')} className="btn bg-white text-purple-600 hover:bg-purple-50 shadow-lg">
                <span>🚗</span>
                <span>Жолоочид ({stats.drivers})</span>
              </button>
              <button onClick={() => navigate('/company/settlements')} className="btn bg-white text-green-600 hover:bg-green-50 shadow-lg">
                <span>💵</span>
                <span>Төлбөр тооцоо</span>
              </button>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* CHARTS & LISTS */}
        {/* ============================================ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 7 хоногийн хүргэлт */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-2xl">📈</span>
              <span>7 хоногийн хүргэлт</span>
            </h3>
            {weeklyStats.length > 0 ? (
              <>
                <div className="space-y-3">
                  {weeklyStats.map((item, index) => {
                    const maxValue = Math.max(...weeklyStats.map(d => d.total), 1);
                    const width = (item.total / maxValue) * 100;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-16">{item.day}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full flex items-center justify-end px-3 transition-all duration-500" 
                            style={{ width: `${Math.max(width, 5)}%` }}
                          >
                            {item.total > 0 && (
                              <span className="text-white text-sm font-semibold">{item.total}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-green-600 w-8">{item.completed}✓</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Нийт:</span>
                    <span className="font-bold text-gray-900">
                      {weeklyStats.reduce((sum, d) => sum + d.total, 0)} хүргэлт / {weeklyStats.reduce((sum, d) => sum + d.completed, 0)} хүргэсэн
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl mb-2 block">📭</span>
                <p>Хүргэлтийн мэдээлэл байхгүй</p>
              </div>
            )}
          </div>

          {/* Сүүлийн хүргэлтүүд */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-2xl">📋</span>
              <span>Өнөөдрийн хүргэлтүүд</span>
            </h3>
            {recentDeliveries.length > 0 ? (
              <div className="space-y-3">
                {recentDeliveries.map((delivery, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => navigate(`/company/deliveries/${delivery.id}`)}
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">📦</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">{delivery.delivery_number}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(delivery.status)}`}>
                          {getStatusText(delivery.status)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{delivery.customer_name} • {delivery.shop?.shop_name}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatTime(delivery.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{delivery.cod_amount?.toLocaleString()}₮</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl mb-2 block">📭</span>
                <p>Өнөөдөр хүргэлт байхгүй</p>
              </div>
            )}
            <button 
              onClick={() => navigate('/company/deliveries')}
              className="w-full mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Бүгдийг харах →
            </button>
          </div>
        </div>

        {/* ============================================ */}
        {/* TOP SHOPS */}
        {/* ============================================ */}
        {topShops.length > 0 && (
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-2xl">🏪</span>
              <span>Онлайн шопууд</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {topShops.map((shop, index) => (
                <div 
                  key={index}
                  onClick={() => navigate(`/company/shops/${shop.id}`)}
                  className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:shadow-md transition cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <span className="text-lg">🛍️</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{shop.name}</p>
                      <span className={`text-xs ${shop.status === 'active' ? 'text-green-600' : 'text-gray-400'}`}>
                        {shop.status === 'active' ? '● Идэвхтэй' : '○ Идэвхгүй'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CompanyLayout>
  );
};

export default CompanyDashboard;